<template>
  <div v-if="productDetails" class="footer-bar" :class="rootClasses">
    <div class="footer-bar__product-details">
      <u-i-image-handler
        class="footer-bar__product-image"
        img-loading="lazy"
        :img-src="productDetails.productImage"
        :img-alt="productDetails.productName"
        img-format="avif"
        img-densities="1x 2x"
        img-width="60px" />
      <span class="footer-bar__product-name">{{ productDetails.productName }}</span>
      <nuxt-link
        v-if="productDetails.productLink"
        class="footer-bar__product-link"
        :to="productDetails.productLink"
        @click="handleLearnMoreClick"
      >Learn more</nuxt-link>
    </div>
    <div class="footer-bar__media">
      <audio-player
        :track-id="productDetails.soundcloudId"
        :product-details="productDetails"
        element-prefix="footer"
        auto-play />
      <product-like
        class="footer-bar__media-like"
        :product-id="productDetails.productId" />
    </div>
    <div class="footer-bar__buy">
      <buy-product
        :product-id="productDetails.productId"
        :defaultCurrency="productDetails.defaultCurrency"
        :product-name="productDetails.productName"
        :product-variations="productDetails.variations?.nodes"
      />
    </div>
    <span class="footer-bar__product-name--mobile">{{ productDetails.productName }}</span>
    <button
      class="footer-bar__close"
      @click="closeFooter">
      <icon-close />
    </button>
  </div>
</template>

<script>
import { useAudioPlayerStore } from "~/stores/audioplayer";
import { defineAsyncComponent, useProductStore } from '#imports';

const IconClose = defineAsyncComponent(() => import('~/assets/icons/IconClose.svg'));

export default {
  components: {
    IconClose,
  },
  setup() {
    const audioPlayerStore = useAudioPlayerStore()

    return { audioPlayerStore }
  },
  computed: {
    productDetails() {
      return this.audioPlayerStore.loadedProductDetails
    },
    rootClasses () {
      return {
        'footer-bar--hidden': !this.audioPlayerStore.footerBarVisible && !this.audioPlayerStore.isPlaying,
      }
    },
  },
  methods: {
    handleLearnMoreClick() {
      this.storeBasicProductDetails()
    },
    closeFooter () {
      this.audioPlayerStore.destroyLoadedWaveSurfer();
      this.audioPlayerStore.setProductDetails(null);
    },
    storeBasicProductDetails() {
      const productStore = useProductStore()

      const basicProductDetails = {
        productId: this.productDetails.productId,
        databaseId: this.productDetails.productId,
        image: {
          mediaItemUrl: this.productDetails.productImage,
          altText: this.productDetails.productName,
        },
        name: this.productDetails.productName,
        slug: this.productDetails.productLink,
        defaultCurrency: this.productDetails.defaultCurrency,
        soundcloudId: this.productDetails.soundcloudId,
        productVendor: this.productDetails?.productVendor,
        variations: {
          nodes: this.productDetails.productVariations
        },
      }

      productStore.storeBasicProductDetails(basicProductDetails)
    },
  },
};
</script>

<style lang="scss">
@use "~/common/scss/helpers";
@use "~/common/scss/variables";
@use "~/common/scss/mq";

.footer-bar {
  --play-icon-size: #{helpers.px-to-rem(40)};

  align-items: center;
  background: #fff;
  border-top: 1px solid;
  bottom: 0;
  display: flex;
  height: helpers.px-to-rem(80);
  justify-content: space-evenly;
  margin-top: 6rem;
  opacity: 1;
  padding-top: 0.4rem;
  position: fixed;
  transition: opacity .25s, transform .25s;
  width: 100%;
  z-index: 300;

  @include mq.mobileOnly() {
    height: helpers.px-to-rem(140);
    align-items: end;
  }

  &--hidden {
    opacity: 0;
    transform: translateY(6rem);
  }

  &__product-details {
    display: grid;
    grid-template: "image name" auto "image link" auto / 5rem 1fr;
    align-items: center;

    @include mq.mobileOnly() {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      position: absolute;
      left: .75rem;
      bottom: 0.7rem;
    }
  }

  &__product-image {
    grid-area: image;
    place-self: center;

    @include mq.mobileOnly() {
      width: 70px;
    }
  }

  &__product-name {
    font-size: 1rem;
    font-weight: 600;
    grid-area: name;
    align-self: end;

    @include mq.mobileOnly() {
      display: none;
    }
  }

  &__product-link {
    font-size: 0.8rem;
    grid-area: link;
    align-self: start;
  }

  &__media {
    flex-grow: 1;
    display: flex;
    flex-direction: row;
    gap: 1rem;
    justify-content: space-between;
    max-width: 50%;
    @include mq.mobileOnly() {
      max-width: none;
      margin-bottom: 1rem;
    }

    .audio-player {
      width: 100%;

      @include mq.mobileOnly() {
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
    }
  }

  &__media-like {
    --icon-color: currentColor;

    align-items: center;
    display: flex;
    font-size: 1.25rem;

    @include mq.mobileOnly() {
      display: none;
    }
  }

  &__buy {
    --price-font-size: #{helpers.px-to-rem(20)};

    text-align: right;

    @include mq.mobileOnly() {
      .buy-product {
        display: flex;
        flex-direction: column;
        align-items: end;
        justify-content: center;
        position: absolute;
        right: .75rem;
        bottom: 1rem;
      }

      .product-price__current-price {
        line-height: 1rem;
      }
    }

    .buy-product__action {
      height: helpers.px-to-rem(40);
    }
  }

  &__close {
    color: currentColor;
    font-size: 1.75rem;
    position: absolute;
    right: .75rem;
    top: .5rem;
    transition: color .25s;

    &:hover,
    &:active {
      color: variables.$colorRed;
    }
  }

  @include mq.aboveMobile() {
    &__product-name--mobile {
      display: none;
    }
  }

  @include mq.mobileOnly() {
    &__product-name--mobile {
      position: absolute;
      left: .75rem;
      top: .5rem;
      max-width: calc(100% - 60px);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
